import { gql } from '@apollo/client';

import { ClientData } from 'shared/models/client';
import { JobData, JobRecruiterPermissions } from 'shared/models/job';
import { JobDescriptionData } from 'shared/models/job-description';
import { RevivalReason, ScheduledOutreachStat } from 'shared/models/scheduled-outreach';
import { SequenceStage, SequenceType } from 'shared/models/sequence';
import { EmailAddress } from 'shared/types/email-compose';

import { Name } from '../../types/common';

export interface Sequence {
    id: string;
    title: string;
    type: SequenceType;
    stages: SequenceStage[];
    cc: EmailAddress[];
    bcc: EmailAddress[];
    weekDaysOnly: boolean;
}

export interface OutreachEmailAccount {
    email: string;
    name: Name;
    queuingOutreachCount: number;
    maxDailyOutreach: number;
}

export interface OutreachCounts {
    emailAccounts: OutreachEmailAccount[];
    outreaches: ScheduledOutreachStat[];
}

export interface OutreachCandidates {
    counts: { aggregate: { count: number } };
    candidates: Array<{
        person: {
            id: string;
            name: Name;
        };
        assignee: {
            calendarLink: string;
        };
    }>;
}

interface JobSendOutreachDataCommon {
    jobId: string;
    availableCount: number;
    samplePerson: {
        id: string;
        name: Name;
    } | null;
    refreshedAt: number;
}

export interface JobSendOutreachData extends JobSendOutreachDataCommon {
    assigneeId: string;
}

export interface JobSendRevivalData extends JobSendOutreachDataCommon {
    revivalReason: string;
}

export interface JobOutreachCheckRecord
    extends Pick<
        JobData,
        | 'id'
        | 'jobType'
        | 'dailyPerUserEmailLimit'
        | 'onePager'
        | 'recruiterPermissions'
        | 'placementFees'
        | 'accountManagerPercentage'
        | 'recruiterPercentage'
    > {
    description: Pick<JobDescriptionData, 'compensation' | 'description' | 'visa' | 'hiringManager'>;
    client: Pick<ClientData, 'description' | 'hq' | 'founders' | 'funding' | 'salesPitch'>;
}

export interface JobOutreachSchedule {
    id: string;
    jobId: string;
    userId: string;
    user: {
        id: string;
        name: string;
        username: string;
    };
    job: {
        id: string;
        title: string;
        status: JobData['status'];
        dailyEmailLimit: JobData['dailyEmailLimit'];
        recruiterPermissions: JobRecruiterPermissions;
        client: {
            id: string;
            name: string;
        };
    };
    stats: Array<{
        sentInPeriod: number;
        currentlyScheduled: number;
        frequencyDays: number;
    }>;
    count: number;
    sendHour?: number;
    sendMinute?: number;
    frequencyDays: number;
    isActive: boolean;
    lastScheduledAt: number;
}

export interface OutreachSearchResultCount {
    job: {
        id: string;
        title: string;
        status: JobData['status'];
        dailyEmailLimit: JobData['dailyEmailLimit'];
        recruiterPermissions: JobRecruiterPermissions;
        client: {
            id: string;
            name: string;
        };
    };
    user: {
        id: string;
        name: string;
        username: string;
    };
    stats: Array<{
        sentInPeriod: number;
        currentlyScheduled: number;
        frequencyDays: number;
    }>;
    schedule: {
        count: number;
        frequencyDays: number;
        isActive: boolean;
    };
    availableCount: number;
    refreshedAt: number;
    revivalReason: null;
}

export interface RevivalOutreachSearchResultCount {
    job: {
        id: string;
        title: string;
        client: {
            id: string;
            name: string;
        };
    };
    revivalReason: RevivalReason;
    availableCount: number;
    refreshedAt: number;
    user: null;
    stats: Array<{
        sentInPeriod: never;
        currentlyScheduled: never;
        frequencyDays: never;
    }>;
}

export const JOB_SEQUENCES = gql`
    query JobSequences($jobId: String!) {
        sequences(where: { jobId: { _eq: $jobId }, archived: { _eq: false } }) {
            id
            title
            type
            stages
            cc
            bcc
            weekDaysOnly
        }
    }
`;

export const OUTREACH_COUNTS = gql`
    query OutreachCounts($jobId: String!, $userId: String!, $sendingDayStart: bigint!) {
        emailAccounts: get_user_outreach_accounts(args: { user_id: $userId }) {
            name
            email
            queuingOutreachCount
            maxDailyOutreach
        }
        outreaches: scheduled_outreaches(
            where: {
                _and: [
                    {
                        _or: [
                            { status: { _in: [scheduled, inProgress] } }
                            { status: { _eq: complete }, scheduledAt: { _gte: $sendingDayStart } }
                        ]
                    }
                    { _or: [{ jobId: { _eq: $jobId } }, { userId: { _eq: $userId } }] }
                ]
            }
        ) {
            jobId
            userId
            limit
            scheduledAt
            status
            addedCount
        }
    }
`;

export const JOB_FOR_OUTREACH = gql`
    query JobForOutreach($jobId: String!) {
        job: jobs_by_pk(id: $jobId) {
            id
            title
            status
            dailyEmailLimit
            recruiterPermissions
            client {
                id
                name
            }
        }
    }
`;

export const READY_CANDIDATES_CACHE = gql`
    query ReadyCandidatesCache($jobId: String!, $assigneeId: String!) {
        job: job_candidates_for_outreach_cache_by_pk(jobId: $jobId, assigneeId: $assigneeId) {
            jobId
            assigneeId
            availableCount
            samplePerson
            refreshedAt
        }
    }
`;

export const REVIVAL_CANDIDATES_CACHE = gql`
    query RevivalCandidatesCache($jobId: String!, $revivalReason: String!) {
        job: job_revival_candidates_for_outreach_cache_by_pk(jobId: $jobId, revivalReason: $revivalReason) {
            jobId
            revivalReason
            availableCount
            samplePerson
            refreshedAt
        }
    }
`;

export const REFRESH_READY_CANDIDATES_CACHE = gql`
    mutation RefreshReadyCandidatesCache($jobId: String!, $userId: String!, $minAgeMs: bigint) {
        refresh_job_candidates_for_outreach_cache(args: { job_id: $jobId, user_id: $userId, min_age: $minAgeMs }) {
            result
        }
    }
`;

export const REFRESH_REVIVAL_CANDIDATES_CACHE = gql`
    mutation RefreshRevivalCandidatesCache($jobId: String!, $revivalReason: String!, $minAgeMs: bigint) {
        refresh_job_revival_candidates_for_outreach_cache(
            args: { job_id: $jobId, revival_reason: $revivalReason, min_age: $minAgeMs }
        ) {
            result
        }
    }
`;

export const JOB_OUTREACH_CHECKS = gql`
    query JobOutreachChecks($jobId: String!) {
        job: jobs_by_pk(id: $jobId) {
            id
            title
            jobType
            dailyPerUserEmailLimit
            onePager
            recruiterPermissions
            placementFees
            recruiterPercentage
            accountManagerPercentage
            description {
                id: jobId
                compensation
                description
                visa
                hiringManager
            }
            client {
                id
                description
                hq
                founders
                funding
                salesPitch
            }
        }
    }
`;

export const SCHEDULE_OUTREACH = gql`
    mutation ScheduleOutreach($outreach: scheduled_outreaches_insert_input!) {
        insert_scheduled_outreaches(objects: [$outreach]) {
            affected_rows
        }
    }
`;

export interface ScheduledOutreach {
    id: string;
    job: {
        id: string;
        title: string;
        client: {
            name: string;
        };
    };
    user: {
        id: string;
        name: string;
        username: string;
    };
    scheduledAt: number;
    requestedAt: number;
    status: string;
    limit: number;
    addedCount: number;
}

export const SCHEDULED_OUTREACHES = gql`
    query ScheduledOutreaches($where: scheduled_outreaches_bool_exp!) {
        outreaches: scheduled_outreaches(
            where: $where
            order_by: [{ status: desc }, { requestedAt: asc }, { job: { client: { name: asc } } }]
        ) {
            id
            job {
                id
                title
                client {
                    id
                    name
                }
            }
            user {
                id
                name(path: "full")
                username
            }
            scheduledAt
            requestedAt
            status
            limit
            addedCount
        }
    }
`;

export const UPDATE_SCHEDULED_OUTREACHES = gql`
    mutation UpdateScheduledOutreaches($id: String!, $updates: scheduled_outreaches_set_input!) {
        update_scheduled_outreaches(where: { id: { _eq: $id } }, _set: $updates) {
            affected_rows
        }
    }
`;

export const JOB_OUTREACH_SCHEDULES = gql`
    query JobOutreachSchedules($where: job_outreach_schedules_bool_exp!) {
        jobOutreachSchedules: job_outreach_schedules(where: $where, order_by: { user: { name: asc } }) {
            id
            jobId
            userId
            user {
                id
                name(path: "full")
                username
            }
            job {
                id
                title
                status
                dailyEmailLimit
                recruiterPermissions
                client {
                    id
                    name
                }
            }
            count
            sendHour
            sendMinute
            frequencyDays
            isActive
            lastScheduledAt
            stats {
                sentInPeriod
                currentlyScheduled
                frequencyDays
            }
        }
    }
`;

export const SAVE_JOB_OUTREACH_SCHEDULE = gql`
    mutation SaveJobOutreachSchedule($schedule: job_outreach_schedules_insert_input!) {
        insert_job_outreach_schedules(
            objects: [$schedule]
            on_conflict: {
                constraint: job_outreach_schedules_jobId_userId_key
                update_columns: [count, isActive, frequencyDays, sendHour, sendMinute]
            }
        ) {
            affected_rows
        }
    }
`;

export const DELETE_JOB_OUTREACH_SCHEDULE = gql`
    mutation DeleteJobOutreachSchedule($jobId: String!, $userId: String!) {
        delete_job_outreach_schedules(where: { jobId: { _eq: $jobId }, userId: { _eq: $userId } }) {
            affected_rows
        }
    }
`;

export const OUTREACH_SEARCH_RESULT_COUNTS = gql`
    query OutreachSearchResultCounts($where: job_candidates_for_outreach_cache_bool_exp!) {
        searchResultCounts: job_candidates_for_outreach_cache(
            where: $where
            order_by: [{ job: { client: { name: asc } } }, { job: { title: asc } }]
        ) {
            job {
                id
                title
                status
                dailyEmailLimit
                recruiterPermissions
                client {
                    id
                    name
                }
            }
            user {
                id
                name(path: "full")
                username
            }
            stats {
                sentInPeriod
                currentlyScheduled
                frequencyDays
            }
            schedule {
                frequencyDays
                count
                isActive
            }
            availableCount
            refreshedAt
        }
    }
`;

export const REVIVAL_OUTREACH_SEARCH_RESULT_COUNTS = gql`
    query RevivalOutreachSearchResultCounts($where: job_revival_candidates_for_outreach_cache_bool_exp!) {
        searchResultCounts: job_revival_candidates_for_outreach_cache(
            where: $where
            order_by: [{ job: { client: { name: asc } } }, { job: { title: asc } }]
        ) {
            job {
                id
                title
                client {
                    id
                    name
                }
            }
            revivalReason
            availableCount
            refreshedAt
        }
    }
`;
